<template>
  <div
    v-if="field.label"
    :class="componentClasses"
  >
    <div class="toggle">
      <Toggle
        v-if="field.isScalar"
        :value="field.show"
        :variant="showVariant"
        @change="next => emit('update:show', next)"
      >
        <span class="toggle-label">Show</span>
      </Toggle>

      <button
        v-else
        class="stk-link toggle-link"
        @click="emit('update:show', !field.show)"
      >
        Toggle all
      </button>
    </div>

    <div class="item-label">
      {{ field.label }}
    </div>

    <div
      class="item-value"
    >
      {{ field.value }}
    </div>

    <div class="toggle">
      <Toggle
        v-if="field.isScalar"
        :value="field.required"
        :variant="requiredVariant"
        @change="next => emit('update:required', next)"
      >
        <span class="toggle-label">Required?</span>
      </Toggle>


      <button
        v-else
        class="stk-link toggle-link"
        @click="emit('update:required', !field.required)"
      >
        Toggle all
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits } from 'vue';
import { Toggle } from '@stakeui';

const props = defineProps({
  field: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:show', 'update:required']);

const isShow = computed(() => props.field.show && !props.field.required);
const isRequired = computed(() => props.field.show && props.field.required);
const showVariant = computed(() => isRequired.value ? 'warning' : 'success');
const requiredVariant = computed(() => isRequired.value ? 'warning' : 'disabled');

const componentClasses = computed(() => [
  'item',
  {
    [`is-scalar`]: props.field.isScalar,
    [`is-not-scalar`]: !props.field.isScalar,
    [`is-array`]: props.field.type === 'array',
    [`is-object`]: props.field.type === 'object',
    [`is-selected`]: isShow.value,
    [`is-required`]: isRequired.value,
    [`has-no-value`]: !props.field.value,
  },
]);
</script>

<style scoped>
.item {
  display: grid;
  align-items: start;
  grid-template-columns: 5rem 16rem 1fr 8rem;
  overflow: hidden;
  gap: 1em;
  padding: 0.375em 0.5em;
  border-radius: 0.5em;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color, color;

  &.is-scalar {
    &:hover {
      background-color: var(--stk-background-alt);
    }

    &.is-selected {
      background-color: var(--stk-success-90);
    }

    &.is-required {
      background-color: var(--stk-warning-90);
    }
  }

  &.is-not-scalar {
    align-items: center;

    &:not(:first-child) {
      margin-top: 1em;
    }

    .item-label {
      color: var(--stk-text-strong);
      font-size: 1.125em;
    }

    &.has-no-value {
      .item-label {
        grid-column: 2;
      }

      .item-value {
        display: none;
      }
    }
  }
}

.toggle {
  font-size: 0.75em;
  height: 1.5rem;
  display: flex;
  align-items: center;
}

.toggle-label {
  font-size: 0.875rem;
  font-weight: 500;
}

.item-label {
  font-weight: 500;
}

.item-value {
  color: var(--stk-text-strong);
  font-weight: 500;
}

.toggle-link {
  color: var(--stk-text-weak);

  &:hover {
    color: var(--stk-text-normal);
  }
}
</style>
